import React from 'react';
import { useDarkMode } from '../../context/DarkModeContext';
import { InternshipPhase } from './InternshipDashboardV2';
import { motion } from 'framer-motion';
import { PhaseGuidance } from '../../hooks/usePhaseGuidance';

interface PhaseDetailsProps {
  currentPhase: InternshipPhase;
  guidance: PhaseGuidance | null;
  loading?: boolean;
}


const PhaseDetails: React.FC<PhaseDetailsProps> = ({ currentPhase, guidance, loading = false }) => {
  const { darkMode } = useDarkMode();

  const bgColor = darkMode ? 'bg-gray-800' : 'bg-white';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const cardBg = darkMode ? 'bg-gray-700/50' : 'bg-gray-50';

  if (loading || !guidance) {
    return (
      <div className={`${bgColor} rounded-lg shadow-sm border ${borderColor} p-6`}>
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-1/4"></div>
          <div className="h-3 bg-gray-300 dark:bg-gray-700 rounded w-3/4"></div>
          <div className="space-y-2">
            <div className="h-3 bg-gray-300 dark:bg-gray-700 rounded"></div>
            <div className="h-3 bg-gray-300 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className={`${bgColor} rounded-lg shadow-sm border ${borderColor} p-6 space-y-6`}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
    >

      {/* Tips */}
      <div>
        <h4 className={`text-sm font-medium ${textColor} mb-3`}>💡 Key Tips</h4>
        <div className="space-y-2">
          {guidance.tips.map((tip, index) => (
            <div key={index} className={`p-3 rounded-lg ${cardBg}`}>
              <h5 className={`text-sm font-medium ${textColor} mb-1`}>
                {tip.title}
              </h5>
              <p className={`text-xs ${mutedTextColor}`}>
                {tip.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Focus Areas */}
      <div>
        <h4 className={`text-sm font-medium ${textColor} mb-3`}>🎯 Focus Areas</h4>
        <ul className="space-y-1">
          {guidance.focus_areas.map((area, index) => (
            <li key={index} className={`text-sm ${mutedTextColor} flex items-start`}>
              <span className="text-blue-500 mr-2">•</span>
              {area}
            </li>
          ))}
        </ul>
      </div>

      {/* Common Mistakes */}
      <div>
        <h4 className={`text-sm font-medium ${textColor} mb-3`}>⚠️ Avoid These Mistakes</h4>
        <ul className="space-y-1">
          {guidance.common_mistakes.map((mistake, index) => (
            <li key={index} className={`text-sm ${mutedTextColor} flex items-start`}>
              <span className="text-red-500 mr-2">•</span>
              {mistake}
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
};

export default PhaseDetails;